import { useEffect, useState } from "react";
import { Refresh } from "iconsax-react";
import { useTranslation } from "react-i18next";
import DBButton from "../../component/global/DBButton";
import DBModal from "../../component/global/DBModal";
import { errorMessage, incentivesApi } from "./api";
import {
    ACTION_LABEL_KEYS,
    displayStatus,
    formatNumber,
} from "./helpers";

function formatWhen(value, locale) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(locale, {
        dateStyle: "medium",
        timeStyle: "short",
    });
}

function targetCaption(rule, t) {
    if (!rule || rule.target_type === "all") {
        return t("incentives.targets.all");
    }
    if (rule.target_label) return rule.target_label;
    const key = {
        item_type: "itemType",
        item: "item",
        form: "form",
        survey: "survey",
    }[rule.target_type];
    return key ? t(`incentives.targets.${key}`) : rule.target_type;
}

function actionCaption(rule, t) {
    if (!rule) return "—";
    if (rule.action === "custom") {
        return rule.custom_action || rule.action_name || "—";
    }
    const key = ACTION_LABEL_KEYS[rule.action];
    return key
        ? t(`incentives.actions.${key}`)
        : rule.action_name || rule.action_code || "—";
}

export default function CampaignDetail({
    show,
    campaignId,
    onHide,
    onEdit,
    onDelete,
}) {
    const { t, i18n } = useTranslation();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";

    useEffect(() => {
        if (!show || !campaignId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError("");
        setCampaign(null);
        incentivesApi
            .campaign(campaignId)
            .then((data) => {
                if (!cancelled) setCampaign(data);
            })
            .catch((requestError) => {
                if (!cancelled) {
                    setError(
                        errorMessage(
                            requestError,
                            t("incentives.error.detail")
                        )
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [show, campaignId, t]);

    const rule = campaign?.rule;
    const status = displayStatus(campaign?.status);
    const canEdit = campaign?.can_edit !== false && campaign?.status !== "archived";

    return (
        <DBModal
            show={show}
            onHide={onHide}
            minWidth={640}
            className="incentive-wizard incentive-detail"
            ariaLabelledby="incentive-detail-title"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    <span id="incentive-detail-title">
                        {t("incentives.detail.title")}
                    </span>
                }
            />
            <DBModal.Content>
                {loading && (
                    <div
                        className="incentive-state"
                        role="status"
                        aria-live="polite"
                    >
                        <span className="incentive-spinner" aria-hidden="true" />
                        {t("loading")}
                    </div>
                )}
                {error && (
                    <div
                        className="incentive-state incentive-state-error"
                        role="alert"
                    >
                        <p>{error}</p>
                        <button
                            type="button"
                            onClick={() => {
                                setError("");
                                setLoading(true);
                                incentivesApi
                                    .campaign(campaignId)
                                    .then(setCampaign)
                                    .catch((requestError) =>
                                        setError(
                                            errorMessage(
                                                requestError,
                                                t("incentives.error.detail")
                                            )
                                        )
                                    )
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <Refresh size={18} aria-hidden="true" />
                            {t("incentives.retry")}
                        </button>
                    </div>
                )}
                {!loading && !error && campaign && (
                    <div className="incentive-review">
                        <dl>
                            <div>
                                <dt>{t("incentives.fields.name")}</dt>
                                <dd>{campaign.name}</dd>
                            </div>
                            <div>
                                <dt>{t("incentives.table.status")}</dt>
                                <dd>
                                    <span
                                        className={`incentive-status incentive-status-${status}`}
                                    >
                                        {t(`incentives.status.${status}`, {
                                            defaultValue: status,
                                        })}
                                    </span>
                                </dd>
                            </div>
                            <div>
                                <dt>{t("incentives.fields.action")}</dt>
                                <dd>{actionCaption(rule, t)}</dd>
                            </div>
                            <div>
                                <dt>{t("incentives.fields.target")}</dt>
                                <dd>{targetCaption(rule, t)}</dd>
                            </div>
                            <div>
                                <dt>{t("incentives.fields.audience")}</dt>
                                <dd>
                                    {rule?.user_group
                                        ? t("incentives.audience.group")
                                        : t("incentives.audience.all")}
                                </dd>
                            </div>
                            <div>
                                <dt>
                                    {t("incentives.fields.participantLimit")}
                                </dt>
                                <dd>
                                    {campaign.participant_limit
                                        ? formatNumber(
                                              campaign.participant_limit,
                                              locale
                                          )
                                        : "—"}
                                </dd>
                            </div>
                            <div>
                                <dt>{t("incentives.fields.perUserLimit")}</dt>
                                <dd>
                                    {formatNumber(
                                        rule?.per_user_limit || 1,
                                        locale
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt>{t("incentives.fields.verification")}</dt>
                                <dd>
                                    {t(
                                        `incentives.verification.${
                                            rule?.evaluation_mode ||
                                            "automatic"
                                        }`
                                    )}
                                </dd>
                            </div>
                            {(rule?.tiers || []).map((tier, index) => (
                                <div key={tier.id || index}>
                                    <dt>
                                        {t("incentives.tier", {
                                            number: index + 1,
                                        })}
                                    </dt>
                                    <dd>
                                        {formatNumber(
                                            tier.amount ?? tier.reward,
                                            locale
                                        )}{" "}
                                        {t("incentives.coins")}
                                    </dd>
                                </div>
                            ))}
                            <div>
                                <dt>{t("incentives.fields.budget")}</dt>
                                <dd>
                                    {formatNumber(
                                        campaign.budget ??
                                            campaign.total_budget,
                                        locale
                                    )}{" "}
                                    {t("incentives.coins")}
                                </dd>
                            </div>
                            <div>
                                <dt>{t("incentives.table.spent")}</dt>
                                <dd>
                                    {formatNumber(
                                        campaign.spent ??
                                            campaign.spent_budget,
                                        locale
                                    )}{" "}
                                    {t("incentives.coins")}
                                </dd>
                            </div>
                            <div>
                                <dt>{t("incentives.fields.starts")}</dt>
                                <dd>
                                    {formatWhen(campaign.starts_at, locale)}
                                </dd>
                            </div>
                            <div>
                                <dt>{t("incentives.fields.ends")}</dt>
                                <dd>
                                    {formatWhen(campaign.ends_at, locale)}
                                </dd>
                            </div>
                        </dl>
                    </div>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                {canEdit && campaign && (
                    <>
                        <DBButton
                            caption={t("incentives.edit")}
                            onClick={() => onEdit?.(campaign.id)}
                        />
                        <DBButton
                            type="secondary"
                            caption={t("incentives.delete")}
                            onClick={() => onDelete?.(campaign)}
                        />
                    </>
                )}
            </DBModal.Footer>
        </DBModal>
    );
}
